import { EMPTY, Observable, concatMap, distinctUntilChanged, filter, pipe, switchMap } from 'rxjs';

import { computed, inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { Selection } from '../models/selection.model';
import { CrossSellingService } from '../services/cross-selling.service';
import { ProjectSelectionsFacade } from '../state/selection/selections.facade';
import { SelectionRef, getSelectionRef } from '../utils/cross-selling.utils';

type CrossSellingState = {
  /** Compatible loose accessories per unit of the active selection: what the link under each unit shows. */
  counts: Partial<Record<string, number>>;
};

const initialState: CrossSellingState = { counts: {} };

/** The counts request and what it depends on: a new `key` reloads the counts. */
type CountsSource = SelectionRef & { key: string };

type QuantityCommand = { looseAccessoryId: string; alternativeId: string; quantity: number };
/** A click on a badge of the row: it joins (`link`) or leaves the alternative. */
type AlternativeLinkCommand = { looseAccessoryId: string; alternativeId: string; link: boolean };

/**
 * The counts follow the units of the selection and its price-list context; they do not depend on the loose-accessory
 * rows, so adding one reloads nothing.
 */
function countsSourceOf(selection: Selection | undefined): CountsSource | null {
  const ref = getSelectionRef(selection);
  if (!ref || !selection) {
    return null;
  }
  const unitIds = Object.values(selection.units ?? {})
    .filter(unit => !unit.deleted)
    .map(unit => unit.uuid)
    .sort();
  const { priceValidityDate, useTestPriceList } = selection.details;
  return { ...ref, key: [ref.selectionId, priceValidityDate, useTestPriceList, ...unitIds].join('|') };
}

/**
 * Cross selling of the active selection: the compatible-accessory counts per unit, and the commands on its
 * loose-accessory rows. Provided by the view that shows them; the counts follow the selection on their own.
 *
 * The commands write nothing here or in the NGXS selection: every connected user, the caller included, gets the
 * change through the selection JSON diff. Their errors reach the user through the global error dialog.
 */
export const CrossSellingStore = signalStore(
  withState(initialState),
  withMethods((store, service = inject(CrossSellingService), selections = inject(ProjectSelectionsFacade)) => {
    const selectionRef = computed(() => getSelectionRef(selections.activeSelectionSignal()));
    const countsSource = computed(() => countsSourceOf(selections.activeSelectionSignal()));

    const loadCounts = rxMethod<CountsSource | null>(
      pipe(
        filter((source): source is CountsSource => source !== null),
        distinctUntilChanged((previous, current) => previous.key === current.key),
        switchMap(({ projectId, selectionId }) =>
          service.getCompatibleAccessoryCounts(projectId, selectionId).pipe(
            tapResponse({
              next: ({ counts }) =>
                patchState(store, { counts: Object.fromEntries(counts.map(({ unitId, count }) => [unitId, count])) }),
              error: () => patchState(store, { counts: {} }),
            })
          )
        )
      )
    );

    /** One at a time, in order: two quantity edits, or a link and its undo, reach the backend as the user made them. */
    const command = <T>(send: (ref: SelectionRef, command: T) => Observable<void>) =>
      rxMethod<T>(
        pipe(
          concatMap(value => {
            const ref = selectionRef();
            return ref ? send(ref, value).pipe(tapResponse({ next: () => undefined, error: () => undefined })) : EMPTY;
          })
        )
      );

    return {
      _trackCounts: () => loadCounts(countsSource),
      updateQuantity: command<QuantityCommand>((ref, { looseAccessoryId, alternativeId, quantity }) =>
        service.updateLooseAccessoryQuantity(ref.projectId, ref.selectionId, looseAccessoryId, {
          alternativeId,
          quantity,
        })
      ),
      remove: command<string>((ref, looseAccessoryId) =>
        service.deleteLooseAccessory(ref.projectId, ref.selectionId, looseAccessoryId)
      ),
      toggleAlternative: command<AlternativeLinkCommand>((ref, { looseAccessoryId, alternativeId, link }) =>
        link
          ? service.linkLooseAccessoryAlternative(ref.projectId, ref.selectionId, looseAccessoryId, { alternativeId })
          : service.unlinkLooseAccessoryAlternative(ref.projectId, ref.selectionId, looseAccessoryId, { alternativeId })
      ),
    };
  }),
  withHooks({
    onInit(store) {
      store._trackCounts();
    },
  })
);
